import React, { useEffect, useMemo, useState } from "react";
import {
  IoIosCheckmarkCircleOutline,
  IoIosCloseCircleOutline,
} from "react-icons/io";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import ContainedButton from "components/layout/form/containedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";

import axios, { Method } from "axios";
import {
  AnswerForm,
  AttemptsLeft,
  Content,
  ContentButtons,
  ContentRight,
  Counter,
  Header,
  Paragraph,
  PopupContainer,
  PopupWrapper,
  QuestionTitle,
  Title,
} from "./styles";

const QuestionPool = ({
  body: { document, userId },
  handleModal,
  setLoading,
  setTokenQuetions,
}: any): JSX.Element => {
  const [questionsPool, setQuestionsPool] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [currentQuestionId, setCurrentQuestionId] = useState("");
  const [questionCounter, setQuestionCounter] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(4);
  const [initialState, setInitialState] = useState(true);
  const [validatedQuestions, setValidatedQuestions] = useState(false);
  const [hasAttemptsLeft, setHasAttemptsLeft] = useState(true);
  const [answerInput, setAnswerInput] = useState("");
  const [answers, setAnswers] = useState([]);
  const [accessToken, setAccessToken] = useState("");
  const [siteId, setSiteId] = useState("");

  const idsToCheck = [
    "750d57f8-2b90-43ca-9ebd-49660dc56b5d",
    "7d50cd5d-30b4-4b78-bcdf-1c9dc7989c5f",
  ];

  const idState = ["14cb7687-901b-4e23-8752-da3696ef377f"];

  const hasCep = idsToCheck.some((id) => currentQuestionId === id);

  const isState = idState.some((id) => currentQuestionId === id);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (hasCep) {
      if (value.length <= 3 && /^\d*$/.test(value)) {
        setAnswerInput(value);
      }
    } else if (isState) {
      if (value.length <= 2 && /^\w*$/.test(value)) {
        setAnswerInput(value.toUpperCase());
      }
    } else {
      setAnswerInput(value.toUpperCase());
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    if (questionsPool.length === 0) {
      getQuestions();
    }

    if (answers.length === 2) {
      sendAnswers();
    }
  }, [questionsPool, answers, accessToken]);

  const getQuestions = async (): Promise<void> => {
    setLoading(true);

    try {
      const result = await api.get(
        `industrial/customer/v1/questions?quantity=2`,
        {
          headers: {
            access_token: accessToken,
          },
        }
      );

      setQuestionsPool(result.data);
      setCurrentQuestion(result.data[0]?.question);
      setCurrentQuestionId(result.data[0]?.id);
    } catch (err) {
      setLoading(false);

      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }

    setLoading(false);
  };

  const nextQuestion = (): void => {
    if (answerInput?.length < 3 && hasCep) return;

    if (answerInput) {
      saveAnswers(answerInput, questionsPool[questionCounter].id);
    }

    if (answerInput && questionCounter < 1) {
      setCurrentQuestion(questionsPool[1].question);
      setCurrentQuestionId(questionsPool[1].id);
      setAnswerInput("");
      setQuestionCounter((prevState) => prevState + 1);
    }
  };

  const saveAnswers = (answer, questionId): void => {
    const dontHaveAnswerId = !answers.some(
      (answer) => answer.questionId === questionId
    );

    if (dontHaveAnswerId) {
      setAnswers((prevState) => [
        ...prevState,
        { questionId, answer: answer.trim() },
      ]);
    }
  };

  const sendAnswers = async (): Promise<void> => {
    setLoading(true);

    const body = {
      document,
      userId,
      answers,
      siteId,
    };

    const headers = {
      headers: {
        access_token: accessToken,
        "Access-Control-Expose-Headers": "attempts_left",
      },
    };

    try {
      const response = await api.post(
        `industrial/customer/v1/questions/answers`,
        body,
        headers
      );

      const tokenQuestion = response.headers["x-token"];

      setTokenQuetions(tokenQuestion);
      setLoading(false);
      setInitialState(false);
      setValidatedQuestions(true);
      toast.success("Todas as respostas estão corretas!");
    } catch (err) {
      setLoading(false);
      setInitialState(false);
      setValidatedQuestions(false);
      setAttemptsLeft(err.response.headers.attempts_left);

      if (err) {
        if (err.response.data.code === "422.051") {
          setHasAttemptsLeft(false);
        } else if (err.response.status === 422) {
          toast.error(err.response.data.message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const tryAgain = (): void => {
    if (hasAttemptsLeft) {
      setAnswerInput("");
      setInitialState(true);
      setAnswers([]);
      setQuestionsPool([]);
      setValidatedQuestions(false);
      setQuestionCounter(0);
    } else {
      handleModal(false, false);
    }
  };

  const generateAccessToken = async (): Promise<void> => {
    const req = axios.create({
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": "pt-BR",
        client_id: process.env.REACT_APP_CLIENT_ID,
      },
      baseURL: process.env.REACT_APP_API_URL_OAUTH,
    });

    const header = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST" as Method,
      headers: header,
    };
    const body = {
      grant_type: "client_credentials",
    };

    try {
      const result = await req.post("oauth/access-token", body, parameters);

      setAccessToken(result.data.access_token);
    } catch (err) {
      const message =
        err.response.data[0]?.message || err.response.data.message;

      toast.error(message);
    }
  };

  const getSiteId = async (): Promise<void> => {
    try {
      const response = await api.get(
        `industrial/customer/v1/customers?type=MI&document=${document}`,
        {
          headers: {
            access_token: accessToken,
          },
        }
      );
      if (response?.data) {
        const [customer] = response.data;
        const { customerAddresses } = customer;

        const { siteId: siteIdResponse } = customerAddresses.filter(
          ({ main }) => main === true
        )[0];

        if (siteIdResponse) {
          setSiteId(siteIdResponse);
        }
      }
    } catch (error) {
      console.log({ error });
    }
  };

  useEffect(() => {
    (async () => {
      await generateAccessToken();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!accessToken) return;
      await getSiteId();
    })();
  }, [accessToken]);

  const renderBody = useMemo(() => {
    if (initialState) {
      const label = `Sua resposta aqui${isState ? " (Ex. SP, RJ, BA)" : ""}`;
      return (
        <ContentButtons>
          <TextField
            name="answer"
            label={label}
            placeholder={label}
            type={hasCep ? "numeric" : "text"}
            value={answerInput}
            inputMode={hasCep ? "numeric" : "text"}
            onChange={handleInputChange}
          />
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => nextQuestion()}
          >
            {answers.length >= 1 ? "Enviar" : "Próximo"}
          </ContainedButton>
        </ContentButtons>
      );
    }

    if (validatedQuestions) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCheckmarkCircleOutline size={84} color="#4FFB23" />
            <section>
              <p>Validado com sucesso!</p>
              <p>
                Clique em continuar para cadastrar um novo e-mail ou telefone.
              </p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => handleModal(false, true)}
          >
            Continuar
          </ContainedButton>
        </Content>
      );
    }

    if (hasAttemptsLeft) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCloseCircleOutline size={84} color="#F25A28" />
            <section>
              <p>Não foi possível continuar.</p>
              <p>Você precisa responder corretamente às perguntas!</p>
              <p>Você tem mais {attemptsLeft || 0} tentativas.</p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => tryAgain()}
          >
            {attemptsLeft > 0 ? "Tente novamente" : "Fechar"}
          </ContainedButton>
        </Content>
      );
    }

    return (
      <Content>
        <AttemptsLeft>
          <IoIosCloseCircleOutline size={84} color="#F25A28" />
          <section>
            <p>
              Você foi bloqueado pois excedeu o número máximo de 5 de
              tentativas.
            </p>
            <p>Converse com nossos especialistas pelo Chat Online.</p>
            <p>
              Nosso time está conectado 24 horas por dia e 7 dias por semana
              para melhor atendê-lo.
            </p>
          </section>
        </AttemptsLeft>
      </Content>
    );
  }, [
    initialState,
    attemptsLeft,
    hasAttemptsLeft,
    validatedQuestions,
    answers,
    answerInput,
  ]);

  return (
    <PopupWrapper>
      <PopupContainer>
        <Header>
          <Title>Cadastro (Perguntas de validação)</Title>
          <ContentRight>
            {initialState && <Counter>{questionCounter + 1}/2</Counter>}
            <MdClose
              size={24}
              onClick={() => {
                if (validatedQuestions) {
                  handleModal(false, true);
                } else {
                  handleModal(false, false);
                }
              }}
            />
          </ContentRight>
        </Header>
        {initialState && (
          <Paragraph>
            Responda as perguntas corretamente para validar seu cadastro
            anterior
          </Paragraph>
        )}
        {initialState && <QuestionTitle>{currentQuestion}</QuestionTitle>}

        <AnswerForm
          initialState={initialState}
          onSubmit={(e) => e.preventDefault()}
        >
          {renderBody}
        </AnswerForm>
      </PopupContainer>
    </PopupWrapper>
  );
};

export default QuestionPool;
